import * as Sentry from "@sentry/react";
import type { RecoveryAction } from "@kovernow/flows/recovery";
import type { TenantWebConfig } from "@kovernow/types/tenant-web-config";

/**
 * Sentry per tenant (CAP-11): DSN and environment come from /config.json;
 * every event is tagged with tenant, region and environment, and errors that
 * end in contact_support carry the backend correlation ID.
 */
export function initSentry(config: TenantWebConfig): void {
  if (!config.sentry.dsn) return;
  Sentry.init({
    dsn: config.sentry.dsn,
    environment: config.sentry.environment,
    release: config.sentry.release,
    sendDefaultPii: false,
    normalizeDepth: 5,
    tracesSampleRate: config.environment === "production" ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [config.apiBaseUrl],
    initialScope: {
      tags: {
        tenant: config.tenantId,
        region: config.region,
        environment: config.sentry.environment,
      },
    },
  });
  // Deploy check (docs/deployment-guide.md): /?sentryTest=1 sends one event.
  if (new URLSearchParams(window.location.search).has("sentryTest")) {
    Sentry.captureMessage(`customer_web Sentry test (${config.tenantId})`, "info");
  }
}

export function reportRecovery(action: RecoveryAction): void {
  if (action.kind !== "nonRecoverable") return;
  Sentry.withScope((scope) => {
    if (action.correlationId) scope.setTag("correlation_id", action.correlationId);
    scope.setTag("error_code", action.errorCode);
    Sentry.captureMessage(`RecoveryAction ${action.action}: ${action.errorCode}`, "warning");
  });
}
